import { useState, useEffect, useRef, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { X, Loader2, Clock, MapPin, CheckCircle2, Calendar as CalendarIcon, Users, Star } from "lucide-react";
import { GiftCodeField } from "@/components/GiftCodeField";
import api, { formatApiError } from "@/lib/api";
import { payFor } from "@/lib/checkout";
import { useAuth } from "@/context/AuthContext";
import ShareReflectionPrompt from "@/components/gauge/ShareReflectionPrompt";
import AgreementSignModal from "@/components/AgreementSignModal";
import IntakeFormFill from "@/components/IntakeFormFill";
import PaymentChoice from "@/components/PaymentChoice";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShareButton } from "@/components/ShareButton";
import { toast } from "sonner";
import { money } from "@/lib/format";
import ListingDescription from "@/components/ListingDescription";
import CaptchaCheck from "@/components/checkout/CaptchaCheck";
import AccountBenefits from "@/components/checkout/AccountBenefits";
import AgreementsStep from "@/components/checkout/AgreementsStep";
import InlineAccountForm from "@/components/checkout/InlineAccountForm";

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d;
};
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const friendly = (d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const TIME_SLOTS = Array.from({ length: 25 }, (_, i) => {
  const mins = 8 * 60 + i * 30;
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
});
const fmt12 = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function BookingModal({ service, onClose, initialLocationId = "" }) {
  const { user, setSession } = useAuth();
  const [dateObj, setDateObj] = useState(tomorrow());
  const [dateOpen, setDateOpen] = useState(false);
  const [time, setTime] = useState("10:00");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [consent, setConsent] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [justJoined, setJustJoined] = useState(false);
  const [bookedEnhancer, setBookedEnhancer] = useState("");
  const svcLocs = service?.service_locations || [];
  const [locId, setLocId] = useState((svcLocs.find((l) => l.location_id === initialLocationId) || svcLocs[0])?.location_id || "");
  const [providerId, setProviderId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [done, setDone] = useState(false);
  const [pendingPay, setPendingPay] = useState(null);
  const [intakeStep, setIntakeStep] = useState(null);
  const contRef = useRef(null);
  const [agreement, setAgreement] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [createAccount, setCreateAccount] = useState(true);
  const [password, setPassword] = useState("");
  const [cap, setCap] = useState({});
  const [capKey, setCapKey] = useState(0);
  const [agreementsStep, setAgreementsStep] = useState(null);
  const [bookingRef, setBookingRef] = useState(null);
  const date = toISO(dateObj);

  const { data: allLocs } = useQuery({ queryKey: ["public-locations"], queryFn: () => api.get("/locations").then((r) => r.data), staleTime: 300000 });
  const hoursLocId = locId && String(locId).length === 24 ? locId : (service?.center_location_id || "");
  const closedWeekdays = useMemo(() => {
    const hours = (allLocs || []).find((l) => l.id === hoursLocId)?.hours || [];
    if (!hours.length) return new Set();
    const open = new Set(hours.filter((h) => !h.closed).map((h) => Number(h.day)));
    return new Set([0, 1, 2, 3, 4, 5, 6].filter((d) => !open.has(d)));
  }, [allLocs, hoursLocId]);

  const { data: policy } = useQuery({
    queryKey: ["booking-policy", service?.id, providerId, locId],
    queryFn: () => api.get(`/booking-policy`, { params: { enhancer_id: providerId || "", location_id: locId || "", service_id: service?.id || "" } }).then((r) => r.data),
  });
  const minHours = policy?.min_notice_hours ?? 12;
  const minDateTime = new Date(Date.now() + minHours * 3600 * 1000);
  const minDay = new Date(minDateTime); minDay.setHours(0, 0, 0, 0);
  const slotTooSoon = (t) => {
    const [h, m] = t.split(":").map(Number);
    const dt = new Date(dateObj); dt.setHours(h, m, 0, 0);
    return dt < minDateTime;
  };

  const { data: prov } = useQuery({
    queryKey: ["service-providers", service?.id, locId],
    queryFn: () => api.get(`/services/${service.id}/providers?location_id=${encodeURIComponent(locId)}`).then((r) => r.data),
    enabled: !!service?.id,
  });
  const providers = prov?.providers || [];
  const stype = prov?.service_type || "";
  const foundationNoChoice = stype === "foundation" && !prov?.allow_provider_choice;
  const showChooser = providers.length > 1 && !foundationNoChoice;

  const { data: creditsData } = useQuery({
    queryKey: ["my-package-credits"],
    queryFn: () => api.get("/me/package-credits").then((r) => r.data),
    enabled: !!user,
  });
  const eligibleCredit = (creditsData?.credits || []).find((c) => c.sessions_remaining > 0);
  const creditCoversFree = !!(eligibleCredit && (eligibleCredit.eligible_service_ids || []).includes(service?.id));
  const [useCredit, setUseCredit] = useState(true);
  const [giftCode, setGiftCode] = useState("");
  const [repeatWeeks, setRepeatWeeks] = useState(0);

  // Only show truly-available start times (provider availability minus conflicts, time-off, notice).
  const selProv = providers.find((p) => p.enhancer_id === providerId);
  const slotApprovalId = selProv?.approval_id || service?._approval_id || "";
  const { data: slotData, isFetching: slotsLoading } = useQuery({
    queryKey: ["open-slots", service?.id, date, locId, providerId, slotApprovalId],
    queryFn: () => api.get(`/services/${service.id}/open-slots`, {
      params: { date, location_id: locId, enhancer_id: providerId || "", approval_id: slotApprovalId || "" },
    }).then((r) => r.data),
    enabled: !!service?.id && !!date,
  });
  const openSlots = slotData?.slots || [];
  const dateBlocked = !!slotData?.blocked;

  // Blackout dates (center closures + this provider's self-blocks) — greyed out in the calendar.
  const { data: blackoutData } = useQuery({
    queryKey: ["blocked-dates", service?.id, locId, providerId],
    queryFn: () => api.get("/blackouts/public", { params: { service_id: service?.id || "", location_id: locId || "", enhancer_id: providerId || "" } }).then((r) => r.data),
    enabled: !!service?.id,
  });
  const blockedDates = new Set(blackoutData?.dates || []);

  // Keep the selected time valid: snap to the first open slot when the list changes.
  useEffect(() => {
    if (openSlots.length && !openSlots.includes(time)) setTime(openSlots[0]);
  }, [slotData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-select provider: GAUGE match → foundation default → first available.
  useEffect(() => {
    if (!providers.length) { setProviderId(""); return; }
    const preset = service?._approval_id && providers.find((p) => p.approval_id === service._approval_id);
    if (preset) { setProviderId(preset.enhancer_id); return; }
    if (foundationNoChoice && prov?.default_provider_id) {
      const dp = providers.find((p) => p.enhancer_id === prov.default_provider_id);
      setProviderId((dp || providers[0]).enhancer_id); return;
    }
    if (!providers.find((p) => p.enhancer_id === providerId)) setProviderId(providers[0].enhancer_id);
  }, [prov]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!service) return null;

  const isMember = user && ["community", "enhancer", "staff", "council", "admin"].includes(user.role);
  const chosenLoc = svcLocs.find((l) => l.location_id === locId) || svcLocs[0];
  const selectedProvider = providers.find((p) => p.enhancer_id === providerId);
  const discPct = prov?.member_discount_pct ?? service.member_discount_pct ?? 0;
  const rawBase = selectedProvider
    ? selectedProvider.price
    : (chosenLoc && chosenLoc.price != null && chosenLoc.price !== "" ? Number(chosenLoc.price) : service.price);
  const basePrice = Number(rawBase) || 0;
  const displayPrice = isMember && discPct > 0 ? (basePrice * (1 - discPct / 100)).toFixed(2) : basePrice.toFixed(2);
  const creditUpcharge = eligibleCredit && !creditCoversFree
    ? Math.max(0, Number((Number(displayPrice) - (eligibleCredit.session_value || 75)).toFixed(2)))
    : 0;
  const approvalId = selectedProvider?.approval_id || service._approval_id || "";

  const doBook = async (authed = !!user) => {
    if (slotTooSoon(time)) {
      setError(`Appointments need at least ${minHours} hours' notice. Please choose a later time.`);
      return;
    }
    const redeeming = eligibleCredit && useCredit;
    const guestName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const { data } = await api.post("/bookings", {
      service_id: service.id, date, start_time: time, name: user ? name : (guestName || name), phone,
      location_id: locId, approval_id: approvalId,
      credit_id: redeeming ? eligibleCredit.id : "",
      repeat_weeks: repeatWeeks, repeat_interval: 1, gift_card_code: giftCode,
      ...(authed ? {} : { email: email.trim(), captcha_token: cap.token, captcha_answer: cap.answer, website: cap.website }),
    });
    const booking = data.booking;
    setBookedEnhancer(booking.enhancer_id || "");
    setBookingRef({ id: booking.id, token: booking.agreement_token, guest: !!data.guest, awaiting: booking.agreements_status === "awaiting" });
    const continueFlow = async () => {
      if (booking.payment_status === "free" || booking.payment_status === "package" || booking.payment_status === "paid") {
        setDone(true);
        const extra = data.series_created ? ` Plus ${data.series_created} weekly follow-ups scheduled.` : "";
        toast.success((booking.payment_status === "package" ? "Session booked with your package — enjoy!" : "Your session is booked — see you there!") + extra);
      } else if (authed) {
        // Logged-in members may pay a deposit now and settle the balance at their visit.
        setPendingPay(booking.id);
      } else {
        await payFor("booking", booking.id, true, "", booking.agreement_token);
      }
    };
    const afterAgreements = async () => {
    // First session with this enhancer (or a requested update): short intake form first.
    let intake = null;
    if (authed) try {
      const { data: ic } = await api.get("/intake/check", { params: { enhancer_id: providerId || "", location_id: locId || "", service_id: service?.id || "" } });
      if (ic.required && ic.questions?.length) intake = ic;
    } catch { /* guests without a session skip the intake prompt */ }
    if (intake) {
      contRef.current = continueFlow;
      setIntakeStep({ bookingId: booking.id, questions: intake.questions });
    } else {
      await continueFlow();
    }
    };
    if (data.pending_agreements?.length) {
      contRef.current = afterAgreements;
      setAgreementsStep({ bookingId: booking.id, token: data.guest ? booking.agreement_token : "", pending: data.pending_agreements, name: booking.name });
    } else {
      await afterAgreements();
    }
  };

  const leaveAgreements = async (signedAll) => {
    setAgreementsStep(null);
    if (signedAll) setBookingRef((r) => (r ? { ...r, awaiting: false } : r));
    const c = contRef.current; contRef.current = null;
    if (c) { setLoading(true); try { await c(); } catch (err) { await handleBookingError(err); } finally { setLoading(false); } }
  };

  const handleBookingError = async (err) => {
    const detail = err.response?.data?.detail;
    if (detail && (detail.code === "agreement_required" || detail.code === "pma_required")) {
      try {
        const { data: ag } = await api.get(`/agreements/${detail.agreement_id}`);
        setAgreement(ag);
      } catch {
        setError("This booking requires a signed agreement.");
      }
    } else if (detail && detail.code === "captcha_failed") {
      setError(detail.message); setCapKey((k) => k + 1);
    } else if (detail && detail.code === "room_conflict") {
      const c = detail.conflict;
      setConflict(true);
      setError(c?.time
        ? `${detail.message} There's a ${c.type} ("${c.title}") at ${c.time}. Please choose another time.`
        : detail.message || "That room is already booked at this time. Please choose another slot.");
    } else if (detail && detail.code === "member_access_required") {
      const lbl = { discovery: "Discovery members", community: "Community members", all_members: "members" }[detail.access_level] || "members";
      setError(`Member Access Required — this service is for ${lbl} only. Please join or upgrade your membership to book.`);
    } else {
      const status = err.response?.status || 0;
      const msg = formatApiError(detail) || err.message || "";
      // Never surface configuration/internal errors to booking clients.
      const looksTechnical = status >= 500 || /no room|room linked|not configured|missing room|internal|traceback|objectid/i.test(msg);
      setError(looksTechnical
        ? "This service is currently unavailable for online booking. Please contact us to schedule."
        : msg);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setConflict(false);
    let authed = !!user;
    // Guest: create an account inline (name, email, password + quick check) or continue as a guest.
    if (!user) {
      const em = email.trim();
      if (!firstName.trim() || !lastName.trim()) { setError("Please enter your first and last name."); return; }
      if (!em || !em.includes("@")) { setError("Please enter a valid email so we can confirm your booking."); return; }
      if (phone.replace(/\D/g, "").length < 7) { setError("Please enter a phone number so we can reach you about your booking."); return; }
      if (!consent) { setError("Please agree to be contacted about your booking to continue."); return; }
      if (!acceptedTerms) { setError("Please accept the Terms of Service to continue."); return; }
      if (!cap.answer) { setError("Please answer the quick check question."); return; }
      if (createAccount && password.length < 12) { setError("Please choose a password with at least 12 characters, including letters and numbers."); return; }
      setLoading(true);
      if (createAccount) {
        try {
          const { data } = await api.post("/auth/quick-register", {
            first_name: firstName.trim(), last_name: lastName.trim(), email: em, password, phone: phone.trim(),
            accepted_terms: acceptedTerms, origin: window.location.origin,
            captcha_token: cap.token, captcha_answer: cap.answer, website: cap.website,
          });
          setSession(data);
          setJustJoined(true);
          authed = true;
        } catch (err) {
          const d = err.response?.data?.detail;
          setError(formatApiError(d?.message ? d.message : d) || "We couldn't set up your account. Please try again.");
          setCapKey((k) => k + 1);
          setLoading(false);
          return;
        }
      }
    } else {
      setLoading(true);
    }
    try {
      await doBook(authed);
    } catch (err) {
      await handleBookingError(err);
    } finally {
      setLoading(false);
    }
  };

  const afterSign = async () => {
    setAgreement(null); setLoading(true);
    try { await doBook(); }
    catch (err) { await handleBookingError(err); }
    finally { setLoading(false); }
  };

  const findNext = async () => {
    try {
      const { data } = await api.get(`/services/${service.id}/next-available`, {
        params: { location_id: locId, enhancer_id: providerId || "", approval_id: slotApprovalId || "" },
      });
      if (data.date) {
        setDateObj(new Date(`${data.date}T00:00:00`));
        setTime(data.time);
        toast.success(`Next opening: ${friendly(new Date(`${data.date}T00:00:00`))} at ${fmt12(data.time)}`);
      } else {
        toast.error("No openings in the next 30 days at this location with this provider.");
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-sm overflow-y-auto p-3 sm:p-6" data-testid="booking-modal">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-5 sm:p-8 eyw-reveal my-auto">
        <button onClick={onClose} data-testid="booking-close" className="absolute top-3 right-3 sm:top-4 sm:right-4 grid place-items-center h-9 w-9 rounded-full text-[#4A5D6B] hover:text-[#144F75] hover:bg-black/5"><X size={20} /></button>
        {agreementsStep ? (
          <AgreementsStep bookingId={agreementsStep.bookingId} token={agreementsStep.token} initialPending={agreementsStep.pending}
            defaultName={agreementsStep.name} onDone={() => leaveAgreements(true)} onLater={() => leaveAgreements(false)} />
        ) : intakeStep ? (
          <IntakeFormFill bookingId={intakeStep.bookingId} questions={intakeStep.questions}
            title={`Before your first ${service.title}${selectedProvider ? ` with ${selectedProvider.name}` : ""}`}
            onDone={async () => { setIntakeStep(null); const c = contRef.current; contRef.current = null; if (c) await c(); }}
            onSkip={async () => { setIntakeStep(null); const c = contRef.current; contRef.current = null; if (c) await c(); }} />
        ) : pendingPay ? (
          <PaymentChoice item={service} quoteRef={{ booking_id: pendingPay }}
            onDeposit={(code) => payFor("booking", pendingPay, false, code)}
            onFull={(code) => payFor("booking", pendingPay, true, code)} />
        ) : done ? (
          <div className="text-center py-8" data-testid="booking-success">
            <CheckCircle2 size={48} className="text-[#144F75] mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-[#1A242B]" data-testid="booking-success-heading">{bookingRef?.awaiting ? "Booking held — sign to confirm" : "You're booked."}</h3>
            <p className="text-[#4A5D6B] mt-2">{service.title}{selectedProvider ? ` with ${selectedProvider.name}` : ""} on {friendly(dateObj)} at {fmt12(time)} ET.</p>
            <p className="text-sm text-[#4A5D6B] mt-2" data-testid="booking-confirmation-note">A confirmation is on its way to your email, and you'll find this session under <b>My Dashboard</b>.</p>
            {bookingRef?.awaiting && (
              <div className="text-sm text-[#B5794E] mt-3 bg-[#D98E73]/10 rounded-xl px-4 py-3 text-left" data-testid="booking-awaiting-agreements">
                Your booking is <b>held until your agreements are signed</b>.{" "}
                {bookingRef.guest
                  ? <a href={`/booking-agreements/${bookingRef.token}`} className="underline font-medium" data-testid="booking-sign-link">Sign now</a>
                  : <>You can sign them anytime from <b>My Dashboard</b>.</>}
                {bookingRef.guest && " We've also emailed you the link."}
              </div>
            )}
            {justJoined && (
              <p className="text-sm text-[#144F75] mt-3 bg-[#144F75]/[0.06] rounded-xl px-4 py-3" data-testid="booking-joined-note">
                Welcome — your free account is ready. You're signed in as <b>{email}</b>.
              </p>
            )}
            {bookingRef?.guest && !user && (
              <div className="mt-5" data-testid="booking-guest-account-offer">
                <InlineAccountForm defaultEmail={email} defaultName={`${firstName} ${lastName}`} defaultPhone={phone} bookingToken={bookingRef.token}
                  onCreated={() => setBookingRef((r) => ({ ...r, guest: false }))} />
              </div>
            )}
            {user && <ShareReflectionPrompt enhancerId={bookedEnhancer} />}
            <button onClick={onClose} className="eyw-btn mt-6">Done</button>
          </div>
        ) : (
          <>
            <p className="eyw-label mb-1">Book a Session</p>
            <h3 className="font-serif text-2xl text-[#1A242B]">{service.title}</h3>
          <div className="mt-1.5"><ShareButton path={`/offerings/${service.slug || service.id}`} testid="offering-share-btn" /></div>
            <ListingDescription text={service.short_description || service.description} item={service} className="text-sm text-[#4A5D6B] mt-2 leading-relaxed line-clamp-4" testid="booking-service-description" />
            <div className="flex items-center gap-4 text-sm text-[#4A5D6B] mt-2">
              <span className="flex items-center gap-1"><Clock size={14} /> {service.duration_min} min</span>
              <span className="flex items-center gap-1"><MapPin size={14} /> {chosenLoc?.name || service.location}</span>
            </div>
            <div className="mt-3 text-2xl font-serif text-[#144F75]">
              {basePrice > 0 ? `${money(displayPrice)}` : "Complimentary"}
              {isMember && discPct > 0 && basePrice > 0 && (
                <span className="text-sm text-[#8FA89B] ml-2 line-through">{money(basePrice)}</span>
              )}
            </div>
            <form onSubmit={submit} className="mt-5 space-y-3">
              {/* Step: Location */}
              {svcLocs.length > 0 && (
                <div>
                  <label className="text-xs text-[#4A5D6B] mb-1 block">1 · Location</label>
                  <Select value={locId} onValueChange={setLocId}>
                    <SelectTrigger data-testid="booking-location" className="eyw-input w-full h-auto py-3">
                      <SelectValue>{chosenLoc?.name || "Select"}{chosenLoc?.mode === "virtual" ? " (online)" : ""}</SelectValue>
                    </SelectTrigger>
                    <SelectContent className="z-[110]">
                      {svcLocs.map((l) => (
                        <SelectItem key={l.location_id} value={l.location_id} data-testid={`booking-loc-${l.location_id}`}>
                          {l.name}{l.mode === "virtual" ? " (online)" : ""}{l.price != null && l.price !== "" ? ` — ${money(Number(l.price))}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Step: Enhancer / provider */}
              {showChooser ? (
                <div data-testid="booking-providers">
                  <label className="text-xs text-[#4A5D6B] mb-1 block">{svcLocs.length > 0 ? "2 · " : ""}Choose your enhancer</label>
                  <div className="space-y-2 max-h-52 overflow-y-auto pr-0.5">
                    {providers.map((p) => {
                      const active = p.enhancer_id === providerId;
                      return (
                        <button type="button" key={p.enhancer_id} onClick={() => setProviderId(p.enhancer_id)}
                          data-testid={`booking-provider-${p.enhancer_id}`}
                          className={`w-full flex items-center gap-3 text-left rounded-xl border px-3 py-2.5 transition-colors ${active ? "border-[#144F75] bg-[#144F75]/[0.05]" : "border-[#E5E0D8] hover:border-[#144F75]/40"}`}>
                          {p.avatar ? <img src={p.avatar} alt={p.name} className="h-9 w-9 rounded-full object-cover" />
                            : <span className="grid place-items-center h-9 w-9 rounded-full bg-[#144F75]/10 text-[#144F75] shrink-0"><Users size={16} /></span>}
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-[#1A242B] truncate">{p.name}</span>
                            <span className="block text-xs text-[#8FA89B] truncate">
                              {p.review_count > 0 ? <span className="inline-flex items-center gap-0.5"><Star size={10} className="fill-[#D98E73] text-[#D98E73]" /> {p.rating} · </span> : null}
                              {p.headline || "Enhancer"}
                            </span>
                          </span>
                          {p.price > 0 && <span className="text-sm font-serif text-[#144F75] shrink-0">{money(Number(p.price))}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : selectedProvider ? (
                <div data-testid="booking-assigned-provider" className="flex items-center gap-3 rounded-xl border border-[#144F75]/20 bg-[#144F75]/[0.04] px-3.5 py-2.5">
                  {selectedProvider.avatar
                    ? <img src={selectedProvider.avatar} alt={selectedProvider.name} className="h-9 w-9 rounded-full object-cover" />
                    : <span className="grid place-items-center h-9 w-9 rounded-full bg-[#144F75]/10 text-[#144F75] shrink-0"><Users size={16} /></span>}
                  <span className="min-w-0">
                    <span className="block text-[11px] uppercase tracking-wide text-[#8FA89B]">Your provider</span>
                    <span className="block text-sm font-medium text-[#1A242B] truncate">{selectedProvider.name}</span>
                  </span>
                </div>
              ) : null}

              {/* Step: Date & Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#4A5D6B] mb-1 block">Date</label>
                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="booking-date" className="eyw-input w-full flex items-center justify-between text-left text-[#1A242B]">
                        <span>{friendly(dateObj)}</span>
                        <CalendarIcon size={16} className="text-[#8FA89B]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[110]" align="start">
                      <Calendar mode="single" selected={dateObj}
                        onSelect={(d) => { if (d) { setDateObj(d); setDateOpen(false); } }}
                        disabled={(d) => d < minDay || blockedDates.has(toISO(d)) || closedWeekdays.has(d.getDay())} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-xs text-[#4A5D6B] mb-1 block">Time</label>
                  {slotsLoading ? (
                    <div className="eyw-input w-full h-auto py-3 flex items-center gap-2 text-[#8FA89B] text-sm" data-testid="booking-slots-loading"><Loader2 size={14} className="animate-spin" /> Finding open times…</div>
                  ) : openSlots.length === 0 ? (
                    <div data-testid="booking-no-slots">
                      <div className="eyw-input w-full h-auto py-3 text-[#B5794E] text-sm">{slotData?.no_hours_here ? "Hours haven't been set at this location yet — try another location." : dateBlocked ? "This date is unavailable at this location." : "No open times on this day."}</div>
                      {!slotData?.no_hours_here && <button type="button" onClick={findNext} data-testid="booking-next-available"
                        className="mt-1.5 text-sm text-[#144F75] font-medium hover:underline inline-flex items-center gap-1">
                        <CalendarIcon size={14} /> Find next available{selectedProvider ? ` with ${selectedProvider.name.split(" ")[0]}` : ""}
                      </button>}
                    </div>
                  ) : (
                    <Select value={time} onValueChange={setTime}>
                      <SelectTrigger data-testid="booking-time" className="eyw-input w-full h-auto py-3">
                        <SelectValue>{fmt12(time)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent className="z-[110] max-h-60">
                        {openSlots.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`booking-time-${t}`}>{fmt12(t)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
              <p className="text-xs text-[#8FA89B] -mt-1" data-testid="min-notice-note">Appointments require at least {minHours} hours' notice.</p>
              <div>
                <label className="text-xs text-[#4A5D6B] mb-1 block">Repeat weekly</label>
                <Select value={String(repeatWeeks)} onValueChange={(v) => setRepeatWeeks(Number(v))}>
                  <SelectTrigger data-testid="booking-repeat" className="eyw-input w-full h-auto py-3">
                    <SelectValue>{repeatWeeks === 0 ? "One-time appointment" : `Weekly · ${repeatWeeks + 1} visits`}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="z-[110]">
                    <SelectItem value="0" data-testid="booking-repeat-0">One-time appointment</SelectItem>
                    <SelectItem value="3" data-testid="booking-repeat-4">Weekly · 4 visits</SelectItem>
                    <SelectItem value="7" data-testid="booking-repeat-8">Weekly · 8 visits</SelectItem>
                    <SelectItem value="11" data-testid="booking-repeat-12">Weekly · 12 visits</SelectItem>
                  </SelectContent>
                </Select>
                {repeatWeeks > 0 && <p className="text-[11px] text-[#8FA89B] mt-1">We'll request the same weekday & time for {repeatWeeks} more weeks. Each visit is confirmed individually.</p>}
              </div>
              {user ? (
                <input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="booking-name" className="eyw-input w-full" />
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <input placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="booking-first-name" className="eyw-input w-full" />
                  <input placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="booking-last-name" className="eyw-input w-full" />
                </div>
              )}
              <input type="tel" autoComplete="tel" required={!user} placeholder={user ? "Phone for reminders (optional)" : "Phone (required)"} value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="booking-phone" className="eyw-input w-full" />
              {!user && (
                <>
                  <input type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="booking-email" className="eyw-input w-full" />
                  <div className="rounded-2xl border border-[#144F75]/20 bg-[#144F75]/[0.03] p-3.5 space-y-2.5" data-testid="booking-account-choice">
                    <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Account choice">
                      <button type="button" role="radio" aria-checked={createAccount} onClick={() => setCreateAccount(true)} data-testid="booking-choice-account"
                        className={`rounded-xl px-3 py-2.5 text-sm font-medium border transition-colors ${createAccount ? "bg-[#144F75] text-white border-[#144F75]" : "bg-white text-[#144F75] border-[#E5E0D8]"}`}>Create free account</button>
                      <button type="button" role="radio" aria-checked={!createAccount} onClick={() => setCreateAccount(false)} data-testid="booking-choice-guest"
                        className={`rounded-xl px-3 py-2.5 text-sm font-medium border transition-colors ${!createAccount ? "bg-[#144F75] text-white border-[#144F75]" : "bg-white text-[#144F75] border-[#E5E0D8]"}`}>Continue as guest</button>
                    </div>
                    <AccountBenefits />
                    {createAccount ? (
                      <input type="password" autoComplete="new-password" placeholder="Create a password (12+ characters, letters & numbers)" value={password}
                        onChange={(e) => setPassword(e.target.value)} data-testid="booking-password" className="eyw-input w-full" />
                    ) : (
                      <p className="text-[11px] text-[#8FA89B]" data-testid="booking-guest-note">Guest bookings can't be managed online, and any required agreements are signed by email link — you can still create an account after booking.</p>
                    )}
                  </div>
                  <CaptchaCheck value={cap} onChange={setCap} refreshKey={capKey} testid="booking-captcha" />
                  <label className="flex items-start gap-2.5 rounded-xl border border-[#E5E0D8] bg-white/60 px-3.5 py-2.5 cursor-pointer" data-testid="booking-consent">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="accent-[#144F75] mt-0.5" data-testid="booking-consent-toggle" />
                    <span className="text-xs text-[#4A5D6B] leading-relaxed">I agree to be contacted about my booking.</span>
                  </label>
                  <label className="flex items-start gap-2.5 rounded-xl border border-[#E5E0D8] bg-white/60 px-3.5 py-2.5 cursor-pointer" data-testid="booking-terms">
                    <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="accent-[#144F75] mt-0.5" data-testid="booking-terms-toggle" />
                    <span className="text-xs text-[#4A5D6B] leading-relaxed">
                      I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-[#144F75] underline">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#144F75] underline">Privacy Policy</a>.
                    </span>
                  </label>
                </>
              )}
              {Number(service.price) > 0 && !(eligibleCredit && useCredit) && <div className="mb-3" data-testid="booking-gift-code-wrap"><GiftCodeField value={giftCode} onChange={setGiftCode} due={Number(service.price)} testid="booking-gift-code" /></div>}
              {eligibleCredit && (
                <label className="flex items-start gap-3 rounded-xl border border-[#5C8A72]/40 bg-[#5C8A72]/[0.06] px-4 py-3 cursor-pointer" data-testid="booking-use-credit">
                  <input type="checkbox" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} className="accent-[#5C8A72] mt-0.5" data-testid="booking-use-credit-toggle" />
                  <span className="text-sm text-[#1A242B]">Use 1 session from my <b>{eligibleCredit.package_name}</b>
                    {creditCoversFree ? (
                      <span className="block text-xs text-[#5C8A72]">{eligibleCredit.sessions_remaining} of {eligibleCredit.sessions_total} sessions remaining — this booking will be fully covered.</span>
                    ) : creditUpcharge > 0 ? (
                      <span className="block text-xs text-[#5C8A72]">{eligibleCredit.sessions_remaining} remaining. Enhance with this session — pay only the <b>{money(creditUpcharge)}</b> difference above your {money((eligibleCredit.session_value || 75))} session value.</span>
                    ) : (
                      <span className="block text-xs text-[#5C8A72]">{eligibleCredit.sessions_remaining} remaining — covered by your session value.</span>
                    )}
                  </span>
                </label>
              )}
              {error && (
                <p className={`text-sm ${conflict ? "text-[#B5794E] bg-[#D98E73]/10 rounded-lg px-3 py-2" : "text-red-600"}`} data-testid="booking-error">{error}</p>
              )}
              {basePrice > 0 && (policy?.deposit_pct > 0 || policy?.cancel_window_hours > 0) && (
                <div className="rounded-xl border border-[#144F75]/20 bg-[#144F75]/[0.04] px-4 py-3 text-xs text-[#4A5D6B] leading-relaxed" data-testid="booking-policy-preview">
                  <p className="font-semibold text-[#144F75] mb-1">Booking terms</p>
                  {isMember && policy?.deposit_pct > 0 && (
                    <p data-testid="policy-deposit">A <b>{policy.deposit_pct}% deposit</b> (${(Number(displayPrice) * policy.deposit_pct / 100).toFixed(2)}) holds your spot; the ${ (Number(displayPrice) * (1 - policy.deposit_pct / 100)).toFixed(2)} balance is due at your visit.</p>
                  )}
                  {policy?.cancel_window_hours > 0 ? (
                    <p data-testid="policy-cancel">Free cancellation up to <b>{policy.cancel_window_hours}h</b> before your appointment. Cancel later{policy?.cancel_fee > 0 ? ` and a ${money(Number(policy.cancel_fee))} late-cancellation fee applies` : " and your deposit is forfeited"}.</p>
                  ) : (
                    (isMember && policy?.deposit_pct > 0) && <p data-testid="policy-cancel">Cancel any time; your deposit is refundable per our standard policy.</p>
                  )}
                </div>
              )}
              <button type="submit" disabled={loading || slotsLoading || openSlots.length === 0} data-testid="booking-submit" className="eyw-btn w-full disabled:opacity-60">
                {loading && <Loader2 className="animate-spin" size={18} />}
                {eligibleCredit && useCredit
                  ? (creditUpcharge > 0 ? `Enhance — pay ${money(creditUpcharge)} difference` : "Confirm — use 1 package session")
                  : basePrice > 0 ? "Continue to Payment" : "Confirm Booking"}
              </button>
              <p className="text-[11px] text-[#8FA89B] leading-relaxed pt-1">
                This service is not a substitute for medical care and does not diagnose, treat, or cure disease. Participation is voluntary; results vary. For medical or crisis needs, contact a licensed provider or emergency services.
              </p>
              <p className="text-[11px] text-[#8FA89B] leading-relaxed">
                Questions? Call <a href="tel:+18599795505" className="text-[#144F75] hover:underline">859-979-5505</a> or email <a href="mailto:info@enhancingyourworld.com" className="text-[#144F75] hover:underline">info@enhancingyourworld.com</a>.
              </p>
            </form>
          </>
        )}
      </div>
      {agreement && <AgreementSignModal agreement={agreement} onClose={() => setAgreement(null)} onSigned={afterSign} />}
    </div>
  );
}
